import rewards from '@/api/rewards'
import { COUPON_TYPES } from '@/constants/coupon'
import {
  use0ToBillion,
  use1To60,
  use1To999x,
} from '@/hooks/common/use-number-field'
import { useNotFalsyField } from '@/hooks/common/use-required-field'
import { navigateBack } from '@/lib/navigate-back'
import ShopifyBridge from '@/lib/shopify-bridge'
import { useField, useForm } from '@shopify/react-form'
import { createContext } from 'react'
import { useTranslation } from 'react-i18next'

export const DEFAULT_FREE_PRODUCT_COUPON = {
  amount: 25,
  combines_with: {
    order_discounts: false,
    product_discounts: false,
    shipping_discounts: false,
  },
  coupon_expire_status: 0,
  coupon_expire_time: 6,
  coupon_type: COUPON_TYPES.Product,
  default_icon_id: 1,
  icon_url: '',
  is_upload_icon: 0,
  points: 1000,
  product_range: 3 as const,
  required: 1,
  required_mini_amount: 10,
  status: 1,
  variant_id: [] as number[],
  product_rid: 0,
  /** 只读字段 */
  title: '',
  image_src: '',
  sku_name: '',
}

export function useFreeProductForm(
  id: string | number | undefined,
  couponData: typeof DEFAULT_FREE_PRODUCT_COUPON,
) {
  const isEdit = !!id
  const { t } = useTranslation()

  const status = useField(couponData.status)
  const coupon_type = useField(couponData.coupon_type)

  const points = use1To999x(couponData.points)
  const amount = use0ToBillion(couponData.amount)

  const coupon_expire_status = useField(couponData.coupon_expire_status)
  const coupon_expire_time = use1To60(
    couponData.coupon_expire_time,
    coupon_expire_status.value,
  )

  const product_range = useField(couponData.product_range)
  const variant_id = useField(couponData.variant_id ?? [])
  const product_rid = useNotFalsyField<number>(
    couponData.product_rid,
    t('coupon:freeProduct'),
  )
  const title = useField(couponData.title)
  const image_src = useField(couponData.image_src)
  const sku_name = useField(couponData.sku_name)
  const combines_with = useField(couponData.combines_with)

  const is_upload_icon = useField(couponData.is_upload_icon)
  const default_icon_id = useField(couponData.default_icon_id)
  const icon_url = useField(couponData.icon_url)

  const form = useForm({
    fields: {
      amount,
      variant_id,
      product_rid,
      title,
      image_src,
      sku_name,
      combines_with,
      coupon_expire_status,
      coupon_expire_time,
      coupon_type,
      default_icon_id,
      icon_url,
      is_upload_icon,
      points,
      product_range,
      status,
    },
    makeCleanAfterSubmit: true,
    onSubmit: async ({ title, image_src, sku_name, ...values }) => {
      try {
        if (isEdit) {
          await rewards.rewardsUpdateCoupon({
            id: Number(id),
            ...values,
          })

          ShopifyBridge.toast.show(t('Common.Success.save'))
        } else {
          await rewards.createCoupon(values)

          ShopifyBridge.toast.show(t('Common.Success.save'))
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              navigateBack('program')
            })
          })
        }
        return {
          status: 'success',
        }
      } catch (e: any) {
        return {
          status: 'fail',
          errors: [
            {
              message: e.message,
            },
          ],
        }
      }
    },
  })

  // useLeaveBlocker(form.dirty)

  return form
}

export const FreeProductFormContext = createContext<
  ReturnType<typeof useFreeProductForm>
>(null as any)
